import { useEffect } from 'react';
import { Alert, App, Button, Drawer, Form, Input, Popconfirm, Switch, TimePicker } from 'antd';
import { DataTable } from '../../components/DataTable';
import type { ColumnsType } from 'antd/es/table';
import { Info, WarningCircle } from '@phosphor-icons/react';
import type { Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import { catalogApi, catalogKeys, type Branch, type BranchShift } from './api';
import { tokens } from '../../theme';

const TIME_FORMAT = 'HH:mm';
const WIRE_TIME_FORMAT = 'HH:mm:ss';

interface FormValues {
  name: string;
  hours: [Dayjs, Dayjs];
}

const describeError = (error: unknown): string =>
  error instanceof ApiError || error instanceof NetworkError
    ? error.message
    : 'Có lỗi không xác định. Thử lại hoặc báo kỹ thuật.';

export function BranchShiftsDrawer({ branch, onClose }: { branch: Branch | null; onClose: () => void }) {
  const [form] = Form.useForm<FormValues>();
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const open = branch !== null;
  const branchId = branch?.id ?? '';

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const shiftsQuery = useQuery({
    queryKey: catalogKeys.branchShifts(branchId),
    queryFn: () => catalogApi.listBranchShifts(branchId),
    enabled: open,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: catalogKeys.branchShifts(branchId) });
    // Bảng chi nhánh đếm ca để biết dòng nào còn thiếu — phải làm mới cùng lúc,
    // không thì cảnh báo "chưa khai ca" vẫn còn sau khi đã khai xong
    await queryClient.invalidateQueries({ queryKey: catalogKeys.allBranchShifts });
  };

  const shifts = shiftsQuery.data ?? [];
  const activeCount = shifts.filter((x) => x.isActive).length;

  const addMutation = useMutation({
    mutationFn: (values: FormValues) => {
      const [start, end] = values.hours;
      return catalogApi.createBranchShift(branchId, {
        name: values.name.trim(),
        startTime: start.format(WIRE_TIME_FORMAT),
        endTime: end.format(WIRE_TIME_FORMAT),
        // Ca khai sau xếp cuối. Người dùng sắp lại bằng cách sửa từng ca —
        // kéo thả cho 2–3 dòng là thừa
        sortOrder: shifts.length + 1,
      });
    },
    onSuccess: async (shift) => {
      await invalidate();
      form.resetFields();
      message.success(`Đã thêm ${shift.displayLabel}.`);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (shift: BranchShift) =>
      catalogApi.updateBranchShift(shift.id, {
        name: shift.name,
        startTime: shift.startTime,
        endTime: shift.endTime,
        sortOrder: shift.sortOrder,
        isActive: !shift.isActive,
      }),
    onSuccess: async (shift) => {
      await invalidate();
      message.success(shift.isActive ? 'Đã bật lại ca.' : 'Đã tắt ca.');
    },
    onError: (error) => message.error(describeError(error)),
  });

  const columns: ColumnsType<BranchShift> = [
    {
      title: 'Ca làm',
      key: 'label',
      render: (_, shift) => (
        <>
          <div style={{ fontWeight: 600 }}>{shift.name}</div>
          <div style={{ fontSize: 11.5, color: tokens.color.ink3, fontFamily: 'monospace' }}>
            {shift.startTime.slice(0, 5)} tới {shift.endTime.slice(0, 5)}
            {/* Ca kết thúc sớm hơn lúc bắt đầu là ca qua nửa đêm — nói rõ để
                người khai không tưởng mình gõ nhầm rồi sửa lại thành sai */}
            {shift.endTime < shift.startTime && ' (sang hôm sau)'}
          </div>
        </>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 160,
      render: (_, shift) => (
        <Popconfirm
          title={shift.isActive ? 'Tắt ca này?' : 'Bật lại ca này?'}
          description={
            shift.isActive
              ? 'Nhân viên sẽ không chọn được ca này khi mở ca. Ca đã chạy vẫn giữ nguyên để đối soát.'
              : 'Nhân viên chọn được ca này trở lại.'
          }
          okText="Đồng ý"
          cancelText="Hủy"
          onConfirm={() => toggleMutation.mutate(shift)}
        >
          <Switch
            checked={shift.isActive}
            loading={toggleMutation.isPending && toggleMutation.variables?.id === shift.id}
            checkedChildren="Đang dùng"
            unCheckedChildren="Đã tắt"
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Drawer
      open={open}
      width={520}
      onClose={onClose}
      title={branch === null ? '' : `Ca làm — ${branch.name}`}
      destroyOnHidden
    >
      {activeCount === 0 ? (
        <Alert
          style={{ marginBottom: tokens.space.sectionGap }}
          type="warning"
          showIcon
          icon={<WarningCircle size={20} weight="fill" aria-hidden />}
          message="Nhân viên chi nhánh này chưa làm được gì"
          description="Chưa có ca nào để chọn thì nhân viên đăng nhập được nhưng không mở ca được, và bị chặn ở mọi thao tác tại quầy — kể cả tra cứu khách."
        />
      ) : (
        <Alert
          style={{ marginBottom: tokens.space.sectionGap }}
          type="info"
          showIcon
          icon={<Info size={20} aria-hidden />}
          message={`${activeCount} ca đang dùng`}
          description="Nhân viên chọn một trong các ca này khi mở ca. Nhãn ca được chép nguyên văn vào từng ca đã chạy, nên sửa giờ ca sau này không làm đổi báo cáo cũ."
        />
      )}

      {shiftsQuery.isPending ? (
        <LoadingState rows={3} />
      ) : shiftsQuery.isError ? (
        <ErrorState error={shiftsQuery.error} onRetry={() => void shiftsQuery.refetch()} />
      ) : (
        <DataTable toolbar={false}
          rowKey="id"
          columns={columns}
          dataSource={shifts}
          pagination={false}
          size="small"
          locale={{ emptyText: 'Chưa khai ca nào.' }}
        />
      )}

      <div
        style={{
          marginTop: tokens.space.sectionGap,
          paddingTop: tokens.space.sectionGap,
          borderTop: `1px solid ${tokens.color.line}`,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Thêm ca</div>

        {addMutation.isError && (
          <Alert
            style={{ marginBottom: 16 }}
            type="error"
            showIcon
            message={describeError(addMutation.error)}
          />
        )}

        <Form<FormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => addMutation.mutate(values)}
        >
          <Form.Item
            label="Tên ca"
            name="name"
            tooltip="Ví dụ: Ca sáng, Ca tối. Nhân viên nhìn thấy tên này kèm giờ khi mở ca."
            rules={[
              { required: true, message: 'Chưa nhập tên ca.' },
              {
                validator: (_, value: string | undefined) =>
                  value === undefined ||
                  !shifts.some((x) => x.name.toLowerCase() === value.trim().toLowerCase())
                    ? Promise.resolve()
                    : Promise.reject(new Error('Chi nhánh này đã có ca cùng tên.')),
              },
            ]}
          >
            <Input maxLength={100} />
          </Form.Item>

          <Form.Item
            label="Giờ ca"
            name="hours"
            // Ca đêm kết thúc sau nửa đêm là bình thường ở quán lẩu, nên KHÔNG
            // để TimePicker tự sắp lại thứ tự hai đầu (order={false})
            tooltip="Ca kết thúc sau nửa đêm thì cứ nhập giờ kết thúc nhỏ hơn giờ bắt đầu."
            rules={[{ required: true, message: 'Chưa nhập giờ ca.' }]}
          >
            <TimePicker.RangePicker
              style={{ width: '100%' }}
              format={TIME_FORMAT}
              minuteStep={5}
              order={false}
            />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={addMutation.isPending}>
            Thêm ca
          </Button>
        </Form>
      </div>
    </Drawer>
  );
}
